import { Link } from 'react-router';
import { useNetworkActiveRuns, useNetworkRuns, usePager } from '../../api/queries';
import type { Network, RunSummary } from '../../api/types';
import { isTerminalRunStatus } from '../../api/types';
import { runStatusPresentation } from '../../components/presentation';
import { ErrorMessage, Loading, Pager, StatusBadge } from '../../components/ui';
import { useCardTable } from '../../lib/cardTable';
import { durationMs, formatDateTime, formatDuration, formatRelative } from '../../lib/format';
import { RUN_KIND_ICON, RunChangesCell } from '../../runs/RunSummaryTable';
import { strings } from '../../strings';
import { WaitingText } from '../components';
import {
  displayActor,
  grantPendingPresentation,
  isGrantPending,
  networkRunLabel,
  waitingWords,
} from '../shared';

const p = strings.networks.page;
const s = strings.networks;

function RunName({ run }: { run: RunSummary }) {
  const Icon = RUN_KIND_ICON[run.kind];
  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1.5">
        <Icon aria-hidden className="size-4 shrink-0 text-fg-muted" />
        <Link className="link font-medium" to={`/runs/${run.id}`}>
          {networkRunLabel(run)}
        </Link>
        {run.scope === 'attachment' && <span className="badge tone-neutral">{s.runLabels.attachment}</span>}
      </span>
      {run.parentRunId && (
        <span className="text-[12px] text-fg-muted">
          {p.queuedAfter}{' '}
          <Link className="link" to={`/runs/${run.parentRunId}`}>
            {p.previousRun}
          </Link>
        </span>
      )}
    </span>
  );
}

function RunStatus({ run }: { run: RunSummary }) {
  if (isGrantPending(run))
    return (
      <StatusBadge
        presentation={grantPendingPresentation(
          s.waiting.grantPendingFailed(
            run.waitingFor ? waitingWords(run.waitingFor) : s.waiting.retryingSoon,
          ),
        )}
      />
    );
  return <StatusBadge presentation={runStatusPresentation[run.status]} />;
}

function triggeredBy(run: RunSummary): string {
  if (run.triggeredBy === 'attachment') return s.runLabels.nebisysAttachment;
  return displayActor(run.createdBy);
}

/**
 * The network's Runs tab (spec 11 6.15.11): Queued and running (`GET /runs?networkId=…&status=active`, polled with
 * the page) with the waiting words, then the history (`GET /networks/{id}/runs`), paged.
 */
export function NetworkRunsTab({ network, poll }: { network: Network; poll: number | false }) {
  const active = useNetworkActiveRuns(network.id, poll);
  const pager = usePager();
  const history = useNetworkRuns(network.id, pager.cursor, poll);
  const cardTable = useCardTable();
  const finished = (history.data?.items ?? []).filter((r) => isTerminalRunStatus(r.status));
  return (
    <div className="space-y-5">
      <section aria-labelledby="net-runs-active">
        <h2 id="net-runs-active" className="mb-2 text-[15px] font-semibold">
          {p.queuedAndRunning}
        </h2>
        {active.error ? (
          <ErrorMessage error={active.error} onRetry={() => void active.refetch()} />
        ) : !active.data ? (
          <Loading />
        ) : active.data.items.length === 0 ? (
          <p className="help">{p.noActiveRuns}</p>
        ) : (
          <ul className="net-active-runs" data-testid="network-active-runs">
            {active.data.items.map((run) => (
              <li key={run.id} className="card flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <RunName run={run} />
                <StatusBadge presentation={runStatusPresentation[run.status]} />
                {run.status === 'running' && run.progress && run.progress.total > 0 && (
                  <span className="text-[12px] text-fg-muted">
                    {run.progress.completed}/{run.progress.total}
                  </span>
                )}
                {run.status === 'pending' && (
                  <span className="text-[12px]">
                    <WaitingText waitingFor={run.waitingFor} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="net-runs-history">
        <h2 id="net-runs-history" className="mb-2 text-[15px] font-semibold">
          {p.history}
        </h2>
        {history.error ? (
          <ErrorMessage error={history.error} onRetry={() => void history.refetch()} />
        ) : !history.data ? (
          <Loading />
        ) : finished.length === 0 && !pager.hasPrevious ? (
          <p className="help">{p.noRuns}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line bg-surface">
            <table ref={cardTable} className="table table-cards" data-testid="network-runs-table">
              <thead>
                <tr>
                  <th scope="col">{p.runColumns.run}</th>
                  <th scope="col">{p.runColumns.status}</th>
                  <th scope="col">{p.runColumns.triggeredBy}</th>
                  <th scope="col">{p.runColumns.version}</th>
                  <th scope="col">{p.runColumns.started}</th>
                  <th scope="col">{p.runColumns.duration}</th>
                  <th scope="col">{p.runColumns.changes}</th>
                </tr>
              </thead>
              <tbody>
                {finished.map((run) => (
                  <tr key={run.id}>
                    <td>
                      <RunName run={run} />
                    </td>
                    <td>
                      <RunStatus run={run} />
                    </td>
                    <td>{triggeredBy(run)}</td>
                    <td>{strings.common.dash}</td>
                    <td title={formatDateTime(run.startedAt)}>
                      {run.startedAt ? formatRelative(run.startedAt) : p.queued}
                    </td>
                    <td>{formatDuration(durationMs(run.startedAt, run.finishedAt))}</td>
                    <td>
                      <RunChangesCell run={run} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {history.data && (
          <Pager
            hasPrevious={pager.hasPrevious}
            nextCursor={history.data.nextCursor}
            onPrevious={pager.previous}
            onNext={pager.next}
          />
        )}
      </section>
    </div>
  );
}
